import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function YachtFinancing() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/d9957e0f5adaf06b26218bc1d413fbd3.jpeg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl mb-8 tracking-tight">
            Yacht Financing
          </h1>
          <p className="text-xl md:text-2xl font-light tracking-wide">
            Structured solutions for complex yacht transactions.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg md:text-xl leading-relaxed text-gray-900 mb-8">
            SYG Yachts provides strategic support in yacht financing, working with leading lenders and financial institutions to structure tailored solutions for acquisitions, new builds, and refinances.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-gray-900">
            We guide clients through the complexities of yacht finance with clarity and discretion.
          </p>
        </div>
      </section>

      {/* Scope of Services */}
      <section className="py-32 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl mb-20 text-center">
            Scope of Services
          </h2>
          
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 max-w-4xl mx-auto">
            <div>
              <h3 className="text-xl font-medium mb-3">Acquisition financing</h3>
              <p className="text-gray-600 leading-relaxed">
                Structured financing solutions for yacht purchases
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">New build and construction financing</h3>
              <p className="text-gray-600 leading-relaxed">
                Capital solutions aligned with build milestones
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Refinance and restructuring solutions</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimizing existing financing arrangements
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Cross-border transaction support</h3>
              <p className="text-gray-600 leading-relaxed">
                Managing international financing complexities
              </p>
            </div>
            
            <div className="md:col-span-2">
              <h3 className="text-xl font-medium mb-3">Coordination with legal and tax advisors</h3>
              <p className="text-gray-600 leading-relaxed">
                Integrated approach with professional advisory teams
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Advantage Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl mb-12 text-center">
            Strategic Advantage
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-gray-900 text-center">
            Well-structured financing enhances flexibility, preserves capital, and supports long-term ownership objectives. Our role is to align financial strategy with the broader transaction.
          </p>
        </div>
      </section>

      {/* Full Width Image */}
      <section className="relative h-96">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/56d73fd1c84c20ea8d702bbd12b87e8f.jpeg')`
          }}
        >
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
      </section>

      {/* Finance Inquiry Form Section */}
      <section className="py-32 px-6 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-8">
              Discuss Financing Options
            </h2>
            <p className="text-lg md:text-xl text-gray-300">
              Connect with our finance specialists to explore structured solutions for your yacht transaction.
            </p>
          </div>
          
          <form 
            id="finance-inquiry-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d63337tsi3r5mpi9uplg"
            method="POST"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
              const originalText = submitBtn.innerText;
              
              try {
                submitBtn.disabled = true;
                submitBtn.innerText = 'Submitting...';
                
                const response = await fetch(form.action, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                });
                
                if (response.ok) {
                  form.reset();
                  submitBtn.innerText = 'Inquiry Submitted!';
                  setTimeout(() => {
                    submitBtn.innerText = originalText;
                    submitBtn.disabled = false;
                  }, 3000);
                } else {
                  throw new Error('Submission failed');
                }
              } catch {
                submitBtn.innerText = 'Error - Try Again';
                setTimeout(() => {
                  submitBtn.innerText = originalText;
                  submitBtn.disabled = false;
                }, 3000);
              }
            }}
            className="space-y-8"
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50 transition-colors"
                  placeholder="+1 (000) 000-0000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Financing Type *</label>
                <select
                  name="financingType"
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/50 transition-colors cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select financing type</option>
                  <option value="Acquisition Financing" className="bg-stone-900">Acquisition Financing</option>
                  <option value="New Build Financing" className="bg-stone-900">New Build Financing</option>
                  <option value="Refinance" className="bg-stone-900">Refinance</option>
                  <option value="Restructuring" className="bg-stone-900">Restructuring</option>
                  <option value="Other" className="bg-stone-900">Other</option>
                </select>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Yacht Length (approx.)</label>
                <select
                  name="yachtLength"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/50 transition-colors cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select length range</option>
                  <option value="Under 30m" className="bg-stone-900">Under 30m</option>
                  <option value="30m - 50m" className="bg-stone-900">30m - 50m</option>
                  <option value="50m - 80m" className="bg-stone-900">50m - 80m</option>
                  <option value="80m - 100m" className="bg-stone-900">80m - 100m</option>
                  <option value="Over 100m" className="bg-stone-900">Over 100m</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Estimated Budget</label>
                <select
                  name="budget"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/50 transition-colors cursor-pointer"
                >
                  <option value="" className="bg-stone-900">Select budget range</option>
                  <option value="Under €5M" className="bg-stone-900">Under €5M</option>
                  <option value="€5M - €15M" className="bg-stone-900">€5M - €15M</option>
                  <option value="€15M - €30M" className="bg-stone-900">€15M - €30M</option>
                  <option value="€30M - €50M" className="bg-stone-900">€30M - €50M</option>
                  <option value="€50M - €100M" className="bg-stone-900">€50M - €100M</option>
                  <option value="Over €100M" className="bg-stone-900">Over €100M</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Additional Details</label>
              <textarea
                name="details"
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50 transition-colors resize-none"
                placeholder="Please share any additional information about your financing needs..."
              ></textarea>
            </div>
            
            <div className="text-center pt-4">
              <button
                type="submit"
                className="bg-white text-stone-900 px-16 py-4 text-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Submit Inquiry
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}